import { useCallback } from "react";
import { useTranslation } from "react-i18next";

/**
 * Displaying a document date (spec §11.4, §13).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A DOCUMENT DATE IS A CALENDAR DAY, NOT AN INSTANT.
 *
 * The API sends `"2026-08-10"` — `invoice_date` is a `@db.Date` and the server
 * already resolved it in Asia/Kolkata (§13). `new Date("2026-08-10")` parses
 * that as UTC MIDNIGHT, so a browser in any negative-offset zone renders it as
 * the 9th. The owner is in IST so this would never bite him, but the son may
 * open the app on a laptop still set to another zone, and an invoice that
 * displays a date one day off the one printed on the PDF is the sort of bug
 * that gets found by a customer.
 *
 * So the string is SPLIT, never parsed into a Date. There is no timezone in
 * this file and there must not be one.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * MONTH NAMES COME FROM THE LOCALE FILES, not from a date library's locale
 * data. §11.4 requires every user-facing string to go through `t()`, and
 * date-fns ships no Marathi locale — `format(d, "d MMM yyyy")` would silently
 * print "Aug" inside an otherwise Marathi screen. Twelve keys in `en.json` and
 * `mr.json` cost nothing and cannot fall back without the locale test noticing.
 *
 * §11.4 also fixes the digits: "Numbers stay Latin digits (1234) in both
 * locales", which is exactly what string splitting gives — no numeral system
 * conversion happens anywhere.
 */

export interface CalendarDay {
  year: number;
  /** 1-based, so it indexes the `date.mon.*` keys directly. */
  month: number;
  day: number;
}

/** `"2026-08-10"` (or an ISO datetime) → its calendar parts, or null. */
export function parseCalendarDay(iso: string | null | undefined): CalendarDay | null {
  if (typeof iso !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (match === null) return null;

  const year = Number(match[1] ?? "");
  const month = Number(match[2] ?? "");
  const day = Number(match[3] ?? "");
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/**
 * `useFormatDay()` → `(iso) => "10 Aug 2026"`.
 *
 * Returns an empty string for a missing date rather than "Invalid Date" or a
 * dash: the caller decides what an absent date should look like, because "no
 * date yet" on a draft and "not applicable" on a header row read differently.
 */
export function useFormatDay(): (iso: string | null | undefined) => string {
  const { t } = useTranslation();
  return useCallback(
    (iso: string | null | undefined): string => {
      const parts = parseCalendarDay(iso);
      if (parts === null) return "";
      return t("date.dayMonthYear", {
        day: String(parts.day),
        month: t(`date.mon.${parts.month}`),
        year: String(parts.year),
      });
    },
    [t],
  );
}

/**
 * Today as `YYYY-MM-DD`, for the date-range filter inputs.
 *
 * `<input type="date">` speaks calendar days in the USER'S OWN zone, which for
 * a filter is the right answer — he is choosing "the days I want to see", not
 * timestamping a document. Document dates are still the server's to decide
 * (§6), and nothing in this file ever produces one.
 */
export function todayInputValue(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * The Asia/Kolkata calendar day an INSTANT falls on — `2026-08-16` from
 * `2026-08-15T19:00:00.000Z`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FOR TIMESTAMPS, NEVER FOR DOCUMENT DATES. `createdAt` / `updatedAt` are ISO
 * instants in UTC; an invoice's `date` is already a calendar day and must go
 * through {@link parseCalendarDay} untouched.
 *
 * WHAT THIS REPLACES, AND WHY IT MATTERED: `formatDay(updatedAt.slice(0, 10))`.
 * Slicing an ISO instant takes its UTC day and then formats it as though it were
 * an IST one — so work done at 00:30 IST on the 16th, stored as
 * `2026-08-15T19:00:00.000Z`, displayed as "15 Aug". IST is UTC+5:30, so EVERY
 * timestamp between 00:00 and 05:30 IST reports the previous day. The owner
 * works late; "started 15 Aug" on a draft he began after midnight on the 16th
 * is the kind of small wrongness that makes someone stop trusting the dates.
 *
 * CLAUDE.md: dates are always Asia/Kolkata, never machine-local — so this does
 * NOT use `getFullYear()` and friends, which would read the BROWSER's zone and
 * be right only by accident on a phone that happens to be set to IST.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function istDayOfInstant(iso: string | null | undefined): string | null {
  if (typeof iso !== "string" || iso === "") return null;
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return null;
  /*
   * Shift by IST's fixed +05:30 and read the UTC parts, rather than formatting
   * through `Intl` with `timeZone: "Asia/Kolkata"`. A runtime built with
   * `small-icu` silently falls back to a zone it does know, which would be
   * wrong with no error — the same failure mode CLAUDE.md flags for
   * `toLocaleString("en-IN")`. India has one zone and has never observed DST,
   * so the fixed offset is exact rather than an approximation.
   */
  const IST_OFFSET_MINUTES = 5 * 60 + 30;
  const shifted = new Date(instant.getTime() + IST_OFFSET_MINUTES * 60_000);
  const year = shifted.getUTCFullYear();
  const month = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  const day = String(shifted.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
